import React, { useEffect, useRef, useState } from "react";

import Editor, { Monaco } from "@monaco-editor/react";
import { DashboardComponentType, DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import {
  DashboardComponentAlign,
  DashboardComponentGroup,
  DashboardComponentGroupLayout,
  DashboardComponentJustify,
} from "api/model/dashboard/DashboardComponentGroup";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableRowEditCompleteEvent,
  DataTableRowExpansionTemplate,
  DataTableRowReorderEvent,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { fmtDatetime } from "utils/time-utils";
import { useDisplayTimezone } from "hooks/useTimezone";
import "./DashboardReportTabEditor.scss";
import { Instant } from "@js-joda/core";
import { schema } from "./schemas/componentSchema";

const DashboardTabsSubForm = ({
  dashboardTabs,
  onChange,
}: {
  dashboardTabs: Array<Partial<DashboardTabModel>>;
  onChange: (o: Array<Partial<DashboardTabModel>>) => void;
}) => {
  const displayTz = useDisplayTimezone();
  const [expandedTabRows, setExpandedTabRows] = useState<any[] | DataTableExpandedRows | undefined>([...dashboardTabs]);

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    replaceTab(e.newData, e.index);
  };

  const textEditor = (options: ColumnEditorOptions) => {
    return <InputText type="text" value={options.value} onChange={e => options.editorCallback!!(e.target.value)} />;
  };

  const replaceTab = (givenTab: Partial<DashboardTabModel>, index: number) => {
    const newTabs = [...dashboardTabs];
    newTabs[index] = givenTab;

    onChange(newTabs);

    setExpandedTabRows(oldExpands => {
      if (!Array.isArray(oldExpands)) {
        return newTabs;
      }

      if (!givenTab.componentGroups || (givenTab.componentGroups && givenTab.componentGroups.length === 0)) {
        return oldExpands.filter(dashboard => dashboard !== givenTab);
      }

      if (!oldExpands.some(dashboard => dashboard === givenTab)) {
        return [...oldExpands, givenTab];
      }

      return oldExpands.map(dashboard => (dashboard === givenTab ? givenTab : dashboard));
    });
  };

  const onAddTabClick = () => {
    const tab: Partial<DashboardTabModel> = {
      title: fmtDatetime(Instant.now(), displayTz),
      componentGroups: [],
    };

    onChange([...dashboardTabs, tab]);
  };

  const onRemoveTabClick = (rowData: DashboardTabModel) => {
    onChange(
      dashboardTabs.filter(function (el) {
        return el !== rowData;
      })
    );
  };

  const onRowReorder = (e: DataTableRowReorderEvent<DashboardTabModel[]>) => {
    onChange([...e.value]);
  };

  const confirmTabDelete = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, rowData: DashboardTabModel) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.do-you-want-to-delete-this-tab"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => onRemoveTabClick(rowData),
    });
  };

  const onAddGroupClick = (rowData: DashboardTabModel) => {
    const tempTab: DashboardTabModel = {
      ...rowData,
      componentGroups: [
        ...rowData.componentGroups,
        {
          components: [],
          layout: { type: DashboardLayoutType.VERTICAL },
        } as unknown as DashboardComponentGroup,
      ],
    };

    const tempTabs = dashboardTabs.slice().map(dashboard => (dashboard === rowData ? tempTab : dashboard));

    onChange(tempTabs);

    setExpandedTabRows(oldExpands => {
      if (!Array.isArray(oldExpands)) {
        return [tempTab];
      }

      if (!oldExpands.some(dashboard => dashboard === rowData)) {
        return [...oldExpands, tempTab];
      }

      return oldExpands.map(dashboard => (dashboard === rowData ? tempTab : dashboard));
    });
  };

  const addButton = (rowData: DashboardTabModel) => (
    <Button
      icon="pi pi-plus"
      label={T("word.group")}
      className="p-button-sm p-button-success"
      onClick={() => {
        onAddGroupClick(rowData);
      }}
    />
  );
  const removeButton = (rowData: DashboardTabModel) => (
    <Button icon="pi pi-trash" className="p-button-sm p-button-danger" onClick={event => confirmTabDelete(event, rowData)} />
  );

  const renderExpansionTemplate = (data: DashboardTabModel, options: DataTableRowExpansionTemplate) => {
    return (
      <TabExpansionTemplate
        tab={{
          title: data.title,
          componentGroups: data.componentGroups,
          reportOptions: data.reportOptions,
        }}
        onChange={replaceTab}
        tabIndex={options.index}
      />
    );
  };

  return (
    <div style={{ height: "500px", overflowY: "auto" }} className="flex flex-column gap-1">
      <Button
        icon="pi pi-plus"
        label={T("word.tab")}
        className="mt-2 p-button-success p-button-sm"
        onClick={() => onAddTabClick()}
      />
      <DataTable
        value={dashboardTabs}
        size="small"
        reorderableRows
        onRowReorder={onRowReorder}
        editMode="row"
        expandedRows={expandedTabRows}
        onRowEditComplete={onRowEditComplete}
        onRowToggle={e => setExpandedTabRows(e.data)}
        rowExpansionTemplate={renderExpansionTemplate}
      >
        <Column expander={rowData => rowData.componentGroups?.length > 0} style={{ width: "3em" }} />
        <Column field="title" header={T("word.tabs")} editor={options => textEditor(options)} />
        <Column rowEditor headerStyle={{ width: "0", minWidth: "6rem" }} bodyStyle={{ textAlign: "center" }} />
        <Column style={{ width: "0" }} body={addButton} />
        <Column style={{ width: "0" }} body={removeButton} />
        <Column rowReorder style={{ width: "3em" }} />
      </DataTable>
    </div>
  );
};

type TabExpansionTemplateType = {
  tab: Omit<DashboardTabModel, "uuid">;
  onChange: (tab: Omit<DashboardTabModel, "uuid">, index: number) => void;
  tabIndex: number;
};

interface GroupWithTitle extends DashboardComponentGroup {
  title: string;
}

const TabExpansionTemplate = (props: TabExpansionTemplateType) => {
  const [dialog, setDialog] = useState(false);
  const tempGroups: Array<GroupWithTitle> = props.tab.componentGroups.map((ob, index) => ({
    ...ob,
    title: `${T("word.component-group")} ${index + 1}`,
  }));
  const [expandedGroupRows, setExpandedGroupRows] = useState<any[] | DataTableExpandedRows | undefined>([...tempGroups]);
  const groupChange = (group: DashboardComponentGroup, index: number) => {
    const newGroups = [...props.tab.componentGroups];
    newGroups[index] = group;

    props.onChange({ ...props.tab, componentGroups: newGroups }, props.tabIndex);

    setExpandedGroupRows(prevState => {
      if (!Array.isArray(prevState)) {
        return newGroups;
      }

      if (!group.components || (group.components && group.components.length === 0)) {
        return prevState.filter(g => g !== group);
      }

      if (!prevState.some(g => g.index === index)) {
        return [...prevState, group];
      }

      return prevState.map(ob => (ob.index === index ? group : ob));
    });
  };

  const onAddComponentClick = (rowData: GroupWithTitle, index: number) => {
    const comp = Object.freeze({ title: "", type: "", config: {} }) as unknown as IDashboardComponent;
    const group: DashboardComponentGroup = { ...rowData, components: [...rowData.components, comp] };
    const groupNew = [...props.tab.componentGroups];
    groupNew[index] = { ...groupNew[index], components: group.components };

    props.onChange({ ...props.tab, componentGroups: groupNew }, props.tabIndex);

    setExpandedGroupRows(oldExpands => [
      ...((oldExpands as any[]) ?? []).filter(oldGroup => oldGroup.title !== rowData.title),
      group,
    ]);
  };

  const onRemoveGroupClick = (index: number) => {
    const groupTemp = [...props.tab.componentGroups];
    groupTemp.splice(index, 1);

    props.onChange(
      {
        ...props.tab,
        componentGroups: groupTemp,
      },
      props.tabIndex
    );
  };

  const onRowReorder = (e: DataTableRowReorderEvent<GroupWithTitle[]>) => {
    setExpandedGroupRows([]);
    props.onChange(
      {
        ...props.tab,
        componentGroups: e.value,
      },
      props.tabIndex
    );
  };

  const confirmGroupDelete = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.do-you-want-to-delete-this-group"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => onRemoveGroupClick(index),
    });
  };

  const editButton = (rowData: DashboardComponentGroup, index: number) => {
    const onGroupLayoutChange = (newLayout: DashboardComponentGroupLayout) => {
      groupChange({ ...rowData, layout: newLayout }, index);
    };

    return (
      <>
        <Button icon="pi pi-cog" className="p-button-sm" onClick={() => setDialog(true)} />
        <Dialog
          visible={dialog}
          onHide={() => setDialog(false)}
          header={T("label.component-group-configuration", { index: index + 1 })}
          style={{ width: "min(100%, 1000px)" }}
        >
          <GroupLayoutConfiguration
            layout={rowData.layout}
            onChange={onGroupLayoutChange}
            closeDialog={() => setDialog(false)}
          />
        </Dialog>
      </>
    );
  };

  const addButton = (rowData: GroupWithTitle, index: number) => (
    <Button
      icon="pi pi-plus"
      label={T("word.component")}
      className="p-button-sm p-button-success"
      onClick={() => {
        onAddComponentClick(rowData, index);
      }}
    />
  );

  const removeButton = (index: number) => (
    <Button icon="pi pi-trash" className="p-button-sm p-button-danger" onClick={event => confirmGroupDelete(event, index)} />
  );

  const renderExpansionTemplate = (data: GroupWithTitle, options: DataTableRowExpansionTemplate) => {
    return <GroupExpansionTemplate group={data} groupIndex={options.index} onChange={groupChange} />;
  };

  return (
    <div className="group-table">
      <DataTable
        value={tempGroups}
        reorderableRows
        onRowReorder={onRowReorder}
        size="small"
        editMode="row"
        expandedRows={expandedGroupRows}
        onRowToggle={e => setExpandedGroupRows(e.data)}
        rowExpansionTemplate={renderExpansionTemplate}
      >
        <Column expander={rowData => rowData.components?.length > 0} style={{ width: "3em" }} />
        <Column
          field="title"
          header={T("word.groups")}
          body={(data, options) => `${T("word.component-group")} ${options.rowIndex + 1}`}
        />
        <Column style={{ width: "0" }} body={(data, options) => addButton(data, options.rowIndex)} />
        <Column style={{ width: "0" }} body={(rowData, options) => editButton(rowData, options.rowIndex)} />
        <Column style={{ width: "0" }} body={(data, options) => removeButton(options.rowIndex)} />
        <Column rowReorder style={{ width: "3em" }} />
      </DataTable>
    </div>
  );
};

type GroupExpansionTemplateType = {
  group: DashboardComponentGroup;
  onChange: (group: DashboardComponentGroup, index: number) => void;
  groupIndex: number;
};

const GroupExpansionTemplate = (props: GroupExpansionTemplateType) => {
  const onRowReorder = (e: DataTableRowReorderEvent<IDashboardComponent[]>) => {
    const components = [...e.value];

    props.onChange({ ...props.group, components }, props.groupIndex);
  };

  const onRemoveComponentClick = (rowData: IDashboardComponent, index: number) => {
    const componentsTemp = [...props.group.components];
    componentsTemp.splice(index, 1);

    props.onChange({ ...props.group, components: componentsTemp }, props.groupIndex);
  };

  const confirmComponentDelete = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    rowData: IDashboardComponent,
    index: number
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.do-you-want-to-delete-this-component"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => onRemoveComponentClick(rowData, index),
    });
  };

  const editButton = (rowData: IDashboardComponent, index: number) => <EditButton rowData={rowData} index={index} {...props} />;

  const removeButton = (rowData: IDashboardComponent, index: number) => (
    <Button
      icon="pi pi-trash"
      className="p-button-sm p-button-danger"
      onClick={event => confirmComponentDelete(event, rowData, index)}
    />
  );

  return (
    <div className="group-table">
      <DataTable value={props.group.components} size="small" editMode="row" reorderableRows onRowReorder={onRowReorder}>
        <Column
          field="title"
          header={T("word.components")}
          body={data => (data.title && data.title !== "" ? data.title : "-")}
        />
        <Column field="type" header={T("word.type")} />
        <Column style={{ width: "0" }} body={(rowData, options) => editButton(rowData, options.rowIndex)} />
        <Column style={{ width: "0" }} body={(rowData, options) => removeButton(rowData, options.rowIndex)} />
        <Column rowReorder style={{ width: "3em" }} />
      </DataTable>
    </div>
  );
};

const EditButton = (props: GroupExpansionTemplateType & { rowData: IDashboardComponent; index: number }) => {
  const { rowData, index } = props;
  const [dialog, setDialog] = useState(false);

  const onComponentChange = (newComponent: IDashboardComponent) => {
    const components = [...props.group.components];
    components[index] = newComponent;

    props.onChange({ ...props.group, components }, props.groupIndex);
  };

  return (
    <>
      <Button icon="pi pi-cog" className="p-button-sm" onClick={() => setDialog(true)} />
      <Dialog
        visible={dialog}
        onHide={() => setDialog(false)}
        header={`${rowData.title || ""} ${T("word.component-configuration")}`}
        style={{ width: "min(100%, 1000px)" }}
      >
        <ComponentConfiguration component={rowData} onChange={onComponentChange} closeDialog={() => setDialog(false)} />
      </Dialog>
    </>
  );
};

type ComponentConfigurationType = {
  component: IDashboardComponent;
  onChange: (newComponent: IDashboardComponent) => void;
  closeDialog: () => void;
};

const ComponentConfiguration = (props: ComponentConfigurationType) => {
  const { title: titleGiven, type: typeGiven, ...config } = props.component;

  const types: { label: string; value: DashboardComponentType }[] = [
    { label: T("label.product-template-table"), value: "ProductTemplateTable" },
    { label: T("label.dataset-line-chart"), value: "DatasetLineChart" },
    { label: T("label.dataset-scatter-chart"), value: "DatasetScatterChart" },
    { label: T("label.dataset-sankey-chart"), value: "DatasetSankeyChart" },
    { label: T("label.dataset-table"), value: "DatasetTable" },
    { label: T("label.dataset-pie-chart"), value: "DatasetPieChart" },
    { label: T("label.dataset-histogram-chart"), value: "DatasetHistogramChart" },
    { label: T("label.dataset-bar-chart"), value: "DatasetBarChart" },
    { label: T("label.free-text"), value: "FreeText" },
    { label: T("label.failing-component"), value: "FailingComponentTest" },
    { label: T("label.value-change-indicator"), value: "ValueChangeIndicator" },
    { label: T("word.separator"), value: "Separator" },
    { label: T("label.periodic-data-chart"), value: "PeriodicDataChart" },
    { label: T("label.current-date-display"), value: "CurrentDateDisplay" },
    { label: T("label.line-chart-legend"), value: "LineChartLegend" },
  ];

  const monacoRef = useRef<Monaco | null>(null);

  const [title, setTitle] = useState(titleGiven || "");
  const [type, setType] = useState<any>(typeGiven);
  const [value, setValue] = useState<string | undefined>(JSON.stringify(config, null, 2));
  const [error, setError] = useState(false);

  useEffect(() => {
    return () => {
      monacoRef.current?.languages.json.jsonDefaults.setDiagnosticsOptions({});
    };
  }, []);

  const onEditorChange = (newValue: string | undefined) => {
    setValue(newValue);
  };

  const onEditorMounted = (editor: any, monaco: Monaco) => {
    monacoRef.current = monaco;
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: "https://json-schema.org/",
          fileMatch: ["*"],
          schema,
        },
      ],
    });
  };

  const onSaveButtonClick = () => {
    const val = JSON.parse(value!!);
    props.onChange({
      title,
      type,
      ...val,
    });
    props.closeDialog();
  };

  return (
    <div className="flex flex-column gap-2">
      <div className="flex gap-2">
        <div className="flex flex-column gap-1">
          <b>{T("word.title")}</b>
          <InputText value={title} onChange={e => setTitle(e.target.value)} />
        </div>

        <div className="flex flex-column gap-1">
          <b>{T("word.type")}</b>
          <Dropdown
            value={type}
            options={types}
            onChange={(e: { value: any }) => setType(e.value)}
            placeholder={T("action.select-a-type")}
          />
        </div>
      </div>

      <Editor
        height="500px"
        key="component-editor"
        defaultLanguage="json"
        value={value}
        onChange={onEditorChange}
        onMount={onEditorMounted}
        onValidate={validated => setError(!!validated.length)}
        theme="vs-dark"
      />

      <div className="flex justify-content-end">
        <Button icon="pi pi-save" label={T("action.save")} onClick={() => onSaveButtonClick()} disabled={error} />
      </div>
    </div>
  );
};

type GroupLayoutConfigurationType = {
  layout: DashboardComponentGroupLayout;
  onChange: (newGroup: DashboardComponentGroupLayout) => void;
  closeDialog: () => void;
};

const GroupLayoutConfiguration = (props: GroupLayoutConfigurationType) => {
  const [type, setType] = useState<DashboardLayoutType | undefined>(props.layout.type);

  const [gap, setGap] = useState<number | null>(props.layout.gap || 2);
  const [justify, setJustify] = useState<DashboardComponentJustify>(props.layout.justify || DashboardComponentJustify.BETWEEN);
  const [align, setAlign] = useState<DashboardComponentAlign>(props.layout.align || DashboardComponentAlign.CENTER);

  const typeOptions = Object.values(DashboardLayoutType).map(layoutType => ({
    label: layoutType,
    value: layoutType,
  }));

  const justifyOptions = Object.values(DashboardComponentJustify).map(justifyType => ({
    label: justifyType,
    value: justifyType,
  }));

  const alignOptions = Object.values(DashboardComponentAlign).map(alignType => ({
    label: alignType,
    value: alignType,
  }));

  const onSaveClick = () => {
    const layout: DashboardComponentGroupLayout = {
      type: type || DashboardLayoutType.VERTICAL,
    };
    if (type === DashboardLayoutType.HORIZONTAL_FLEX) {
      if (align) {
        layout.align = align;
      }
      if (gap != null) {
        layout.gap = gap;
      }

      if (justify) {
        layout.justify = justify;
      }
    }

    props.onChange(layout);
    props.closeDialog();
  };

  return (
    <div className="flex flex-column gap-2">
      <div className="flex flex-column gap-1">
        <b>{T("word.type")}</b>
        <Dropdown
          value={type}
          options={typeOptions}
          onChange={(e: { value: DashboardLayoutType }) => setType(e.value)}
          placeholder={T("action.select-a-type")}
        />
      </div>

      {type === DashboardLayoutType.HORIZONTAL_FLEX && (
        <>
          <div className="flex flex-column gap-1">
            <b>{T("word.gap")}</b>
            <InputNumber value={gap} onChange={e => setGap(e.value)} min={0} max={8} maxFractionDigits={0} />
          </div>

          <div className="flex flex-column gap-1">
            <b>{T("word.justify")}</b>
            <Dropdown
              value={justify}
              options={justifyOptions}
              onChange={(e: { value: DashboardComponentJustify }) => setJustify(e.value)}
              placeholder={T("action.select-a-justify")}
            />
          </div>

          <div className="flex flex-column gap-1">
            <b>{T("word.align")}</b>
            <Dropdown
              value={align}
              options={alignOptions}
              onChange={(e: { value: DashboardComponentAlign }) => setAlign(e.value)}
              placeholder={T("action.select-a-align")}
            />
          </div>
        </>
      )}

      <div className="flex justify-content-end">
        <Button icon="pi pi-save" label={T("action.save")} onClick={() => onSaveClick()} />
      </div>
    </div>
  );
};

export default DashboardTabsSubForm;
